import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Languages, LogOut, LayoutDashboard, Check } from 'lucide-react';
import { useT } from '../studioI18n';
import { useStudioAuth } from '../StudioAuthContext';

const LOGO = `${process.env.PUBLIC_URL || ''}/studio-logo.png`;

export const StudioHeader = () => {
  const { t, lang, setLang } = useT();
  const { customer, logout } = useStudioAuth();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/80" data-testid="studio-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="studio-header-logo">
          <img src={LOGO} alt="PM Studio" className="h-14 w-auto object-contain shrink-0" data-testid="studio-logo-img" />
          <div className="leading-tight min-w-0"><p className="font-display font-extrabold text-slate-900 truncate">{t.brand}</p><p className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-700">{t.studio}</p></div>
        </Link>
        <div className="flex items-center gap-2">
          <motion.button whileTap={{ scale: 0.94 }} type="button" onClick={() => setLang(lang === 'en' ? 'pt-BR' : 'en')} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-full bg-white/80 border border-white/90 text-xs font-bold text-teal-800 shadow-sm" data-testid="studio-lang-toggle"><Languages className="h-4 w-4" /> {lang === 'en' ? 'PT' : 'EN'}</motion.button>
          {customer && (
            <>
              <motion.button whileTap={{ scale: 0.94 }} type="button" onClick={() => navigate('/project')} className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3 rounded-full bg-slate-900 text-white text-xs font-bold" data-testid="studio-account-menu"><LayoutDashboard className="h-4 w-4" /> {t.myProject}</motion.button>
              <motion.button whileTap={{ scale: 0.94 }} type="button" onClick={() => { logout(); navigate('/'); }} className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/80 border border-white/90 text-slate-600 shadow-sm" aria-label={t.logout} data-testid="studio-logout-btn"><LogOut className="h-4 w-4" /></motion.button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const STEPS = ['photos', 'design', 'reveal', 'estimate'];
export const Stepper = ({ current, done = [] }) => {
  const { t } = useT();
  const labels = { photos: t.stPhotos, design: t.stDesign, reveal: t.stReveal, estimate: t.stEstimate };
  const idx = STEPS.indexOf(current);
  return (
    <ol className="flex items-center gap-2 sm:gap-3" data-testid="studio-stepper">
      {STEPS.map((s, i) => {
        const isDone = done.includes(s) || i < idx;
        const active = s === current;
        return (
          <li key={s} className="flex items-center gap-2 sm:gap-3">
            <Link to={`/project/${s}`} className="flex items-center gap-2 group" data-testid={`stepper-${s}`}>
              <motion.span layout className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-extrabold transition-colors ${active ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30' : isDone ? 'bg-teal-100 text-teal-800' : 'bg-white/70 text-slate-400 border border-slate-200'}`}>{isDone && !active ? <Check className="h-4 w-4" /> : i + 1}</motion.span>
              <span className={`text-xs font-bold hidden sm:inline ${active ? 'text-slate-900' : 'text-slate-500'}`}>{labels[s]}</span>
            </Link>
            {i < STEPS.length - 1 && <span className={`h-[2px] w-5 sm:w-10 rounded-full ${i < idx ? 'bg-teal-500' : 'bg-slate-200'}`} />}
          </li>
        );
      })}
    </ol>
  );
};
